import {BaseFormModel} from './base-form-model';
import {MenuItemPrice} from './menu-item-price';
import {MenuItem} from './menu-item';
import {OrderItemSelection} from './order-item-selection';
import {MenuSelectionItem} from './menu-selection-item';
import {MenuItemSelection} from './menu-item-selection';
import {addCurrency, multiplyCurrency, subtractCurrency} from '../_utils/currency-math';
import {OrderItemCredit} from './order-item-credit';
import {OrderItemTopping} from './order-item-topping';
import {Tax} from './tax';
import {Tab} from './tab';
import {calculateTax, includedTaxedAmount, isTaxIncluded} from '../_utils/tax-utils';
import {GiftCard} from './gift-card';
import {MembershipRenewal} from './membership-renewal';
import {MenuTaxLabel} from './menu-tax-label';
import {OrderItemModifier} from './order-item-modifier';
import {groupAndSortModifiers, sortBySortOrder} from '../_utils/sortable-utils';

export class OrderItem extends BaseFormModel<OrderItem> {
    id: number;
    menuItem: MenuItem;
    selectedPrice: MenuItemPrice;
    includedToppings: OrderItemTopping[];
    optionalToppings: OrderItemTopping[];
    modifiers: OrderItemModifier[];
    selections: OrderItemSelection[];
    credits: OrderItemCredit[];
    creditsTotal: number;
    tabCredits: number;
    total: number;
    deposits: number;
    priceOverride: number;
    orderType: string;
    notes: string;
    miscItem = false;
    addIndex: number;
    taxes: Tax[];
    key: string;
    preventAutoCredit: boolean;
    giftCard: GiftCard;
    membershipRenewal: MembershipRenewal;
    membershipOrganizationFee: number;
    membershipHostFee: number;
    membershipFeeIncluded: boolean;
    containsOptionalToppings = false;

    status: string;

    // ONLY for navigation purposes
    orderItemSelectionId: number;
    menuSelectionItem: MenuSelectionItem;
    menuItemSelection: MenuItemSelection;
    orderIndex: number;
    menuSelectionIndex: number;
    count = 1;
    membershipTokenGroupId: number;
    tokenPayment: number;
    tokenPaymentRefunded: number;

    // for auto applied credit checks
    tempCreditCount: number;
    creditsModified: boolean;

    currentTokenGroupId: number;
    currentTokenPayment: number;

    groupedModifiers = [];
    groupedToppings = [];
    ungroupedToppings = [];
    menuCategoryColor: string;
    menuCategoryContrastColor: string;

    // ONLY for rest conversion
    toppings: OrderItemTopping[];

    public constructor(jsonInput: any) {
        super(jsonInput);
        Object.assign(this, jsonInput);
        this.menuItem = !!this.menuItem ? new MenuItem(this.menuItem) : null;
        const selections = [];
        if (this.selections) {
            this.selections.forEach(sel => {
                selections.push(new OrderItemSelection(sel));
            });
        }
        this.selections = selections;

        if (!!jsonInput && !!jsonInput.toppings && jsonInput.toppings.length > 0) {
            this.optionalToppings = [];
            this.includedToppings = [];
            let topping: OrderItemTopping;
            jsonInput.toppings.forEach(t => {
                // check if this is a MenuItemTopping or an OrderItemTopping
                topping = new OrderItemTopping(t);
                topping.init();

                if (topping.menuItemTopping.included) {
                    this.includedToppings.push(topping);
                } else {
                    this.optionalToppings.push(topping);
                }
            });
        }
    }

    init(): void {
        this.menuItem = !!this.menuItem ? new MenuItem(this.menuItem) : null;
        this.membershipRenewal = !!this.membershipRenewal ? new MembershipRenewal(this.membershipRenewal) : null;

        const selections = [];
        if (this.selections) {
            this.selections.forEach(sel => {
                selections.push(new OrderItemSelection(sel));
            });
        }

        this.selections = selections;

        if (this.toppings && this.toppings.length > 0) {
            this.optionalToppings = [];
            this.includedToppings = [];
            let topping: OrderItemTopping;
            this.toppings.forEach(t => {
                // check if this is a MenuItemTopping or an OrderItemTopping
                topping = new OrderItemTopping(t);

                if (topping.menuItemTopping.included) {
                    this.includedToppings.push(topping);
                } else {
                    this.optionalToppings.push(topping);
                }
            });
        }

        if (!this.modifiers) {
            this.modifiers = [];
        }

        this.groupedModifiers = groupAndSortModifiers(this.modifiers);
        this.sortAndGroupToppings();
    }

    sortAndGroupToppings() {
        // no modifiers throw into ungrouped
        const ungroupedToppings = [];
        const groupedToppings = [];
        if (!!this.toppings && this.toppings.length > 0) {
            this.toppings.filter(t => t.included)
                .forEach(t => {
                    const topping = new OrderItemTopping(t);
                    topping.init();
                    if (!topping.modifiers || topping.modifiers.length === 0 || !topping.modifiers.some(m => m.groupByModifier)) {
                        ungroupedToppings.push(topping);
                    } else {
                        // if there are multiple modifier groups, just use the first one
                        const grouping = topping.modifiers.find(f => f.groupByModifier && !f.defaultModifier);
                        if (!!grouping) {
                            let found = groupedToppings.find(f => f.name === grouping.menuModifier.name);
                            if (!found) {
                                found = {
                                    name: grouping.menuModifier.name,
                                    sortOrder: grouping.sortOrder,
                                    toppings: [topping]
                                };
                                groupedToppings.push(found);
                            } else {
                                found.toppings.push(topping);
                            }
                        } else {
                            ungroupedToppings.push(topping);
                        }
                    }
                });
            this.ungroupedToppings = sortBySortOrder(ungroupedToppings);
            this.groupedToppings = sortBySortOrder(groupedToppings);
        } else {
            this.ungroupedToppings = [];
            this.groupedToppings = [];
        }
    }

    get membershipFees() {
        return addCurrency(!!this.membershipOrganizationFee ? this.membershipOrganizationFee : 0, !!this.membershipHostFee
            ? this.membershipHostFee
            : 0);
    }

    compactAddedToppings() {
        return this.addedToppingsFilter().map((t: any) => t.menuItemTopping.topping.name).join(', ');
    }

    compactRemovedToppings() {
        return this.removedToppingsFilter().map((t: any) => t.menuItemTopping.topping.name).join(', ');
    }

    removedToppingsFilter() {
        return !!this.includedToppings ? this.includedToppings.filter(topping => !topping.included) : [];
    }

    includedToppingsFilter() {
        return !!this.includedToppings ? this.includedToppings.filter(topping => topping.included) : [];
    }

    displayIncludedAndModifiedToppingsFilter() {
        return !!this.ungroupedToppings ? this.ungroupedToppings.filter(topping => topping.included
            && topping.menuItemTopping.included
            && topping.modifiers.length > 0 && topping.modifiers.some(m => !m.defaultModifier)) : [];
    }

    includedAndModifiedToppingsFilter() {
        return !!this.includedToppings ? this.includedToppings
            .filter(topping => topping.included && topping.modifiers.length > 0) : [];
    }

    displayAddedToppingsFilter() {
        return !!this.ungroupedToppings
            ? this.ungroupedToppings.filter(topping => topping.included && !topping.menuItemTopping.included)
            : [];
    }

    addedToppingsFilter() {
        return !!this.optionalToppings ? this.optionalToppings.filter(topping => topping.included) : [];
    }

    includedToppingsDisplay() {
        const display = this.compactRemovedToppings();

        return display ? 'No ' + display : '';
    }

    optionalToppingsDisplay() {
        const display = this.compactAddedToppings();

        return display ? 'Add ' + display : '';
    }

    toppingPrice() {
        return this.optionalToppings.reduce((total, topping) => addCurrency(total, topping.additionalPrice), 0);
    }

    calcTotal() {
        let total = this.total;
        if (!!this.selectedPrice && !this.selectedPrice.deposit) {
            total = this.selectedPrice.variable ? this.priceOverride : this.selectedPrice.price;
        }

        if (!!this.modifiers) {
            this.modifiers.forEach(m => {
                total = addCurrency(total, m.additionalPrice);
            });
        }

        this.includedToppingsFilter().forEach(topping => {
            // for now assume all included toppings are no additional price
            // total = addCurrency(total, topping.additionalPrice);
            topping.modifiers.forEach(m => {
                total = addCurrency(total, m.additionalPrice);
            });
        });

        this.addedToppingsFilter().forEach(topping => {
            total = addCurrency(total, topping.additionalPrice);
            topping.modifiers.forEach(m => {
                total = addCurrency(total, m.additionalPrice);
            });
        });

        const parentAmount = !this.selectedPrice || (!!this.selectedPrice && !!this.selectedPrice.variable)
            ? this.priceOverride : this.selectedPrice.price;

        this.selections.forEach(selection => {
            total = addCurrency(total, selection.calculateTotal(!!parentAmount ? parentAmount : 0));
        });

        this.total = total;
        return total;
    }

    calcDeposits() {
        let total = (!!this.selectedPrice && this.selectedPrice.deposit) ? this.selectedPrice.price : this.deposits;
        this.selections.forEach(selection => {
            total = addCurrency(total, selection.calculateDeposit());
        });
        return total;
    }

    totalWithAdditional() {
        return addCurrency(this.calcTotal(), multiplyCurrency((!!this.menuSelectionItem && !!this.menuSelectionItem.additionalPrice
            ? this.menuSelectionItem.additionalPrice
            : 0), this.count));
    }

    calcCredits() {
        return !!this.credits ? this.credits.reduce((total, c) =>
            addCurrency(total, c.amount), 0) : 0;
    }

    maxCreditIndex(): number {
        return !!this.credits ? this.credits.reduce((max, credit) => max = (max > credit.addIndex) ? max : credit.addIndex + 1, 0) : 0;
    }

    totalLessComps() {
        return subtractCurrency(this.calcTotal(), addCurrency(this.calcCredits(), this.tabCredits));
    }

    groupTaxedAmounts(tab: Tab) {
        const taxes = [];
        if (!!this.membershipRenewal && !!this.membershipRenewal.membershipProgram && !!this.membershipRenewal.membershipProgram.taxLabels) {
            this.applyTaxes(this.membershipRenewal.membershipProgram.taxLabels, tab, taxes);
        } else if (!!this.menuItem && !!this.menuItem.taxLabels) {
            this.applyTaxes(this.menuItem.taxLabels, tab, taxes);
        } else if (!!this.taxes) {
            this.taxes.forEach(t => {
                const taxIncluded = !!this.tokenPayment || isTaxIncluded(this.menuItem.taxOverride, t, !!tab
                    ? tab.paymentInformation.paymentType
                    : null);
                const tax = new Tax(t);
                tax.taxIncluded = taxIncluded;
                tax.tax = calculateTax(taxIncluded
                    ? includedTaxedAmount(this.totalLessComps(), t.taxRate)
                    : this.totalLessComps(), t.taxRate);
                tax.taxedTotal = taxIncluded ? includedTaxedAmount(this.totalLessComps(), t.taxRate) : this.totalLessComps();
                taxes.push(tax);
            });
        }
        this.taxes = taxes;
    }

    applyTaxes(taxLabels: MenuTaxLabel[], tab: Tab, taxes: Tax[]) {
        if (!this.tokenPayment || !tab.membershipProgram || tab.membershipProgram.taxTokenPayments) {
            taxLabels.forEach(taxLabel => {
                const taxIncluded = !!this.tokenPayment || isTaxIncluded(!!this.menuItem ? this.menuItem.taxOverride : null, taxLabel, !!tab
                    ? tab.paymentInformation.paymentType
                    : null);

                const tax = new Tax({
                    name: taxLabel.name,
                    taxIncluded,
                    tax: calculateTax(taxIncluded
                        ? includedTaxedAmount(this.totalLessComps(), taxLabel.taxRate)
                        : this.totalLessComps(), taxLabel.taxRate),
                    taxedTotal: taxIncluded ? includedTaxedAmount(this.totalLessComps(), taxLabel.taxRate) : this.totalLessComps(),
                    taxRate: taxLabel.taxRate,
                    taxIncludedCash: taxLabel.taxIncludedCash,
                    taxIncludedGiftCard: taxLabel.taxIncludedGiftCard,
                    taxIncludedCharge: taxLabel.taxIncludedGiftCard
                });
                if (tax.tax > 0) {
                    taxes.push(tax);
                }
            });
        }
    }

    isModified() {
        return !!this.modifiers && this.modifiers.length > 0 && this.modifiers.some(m => !m.defaultModifier);
    }
}
